import { useEffect, useState } from "react";
import axios from "axios";
import config from "../../config";
import { useAuth } from "../../context/AuthContext";
import { WebsiteCms, AlertRecipients, NurseAvailabilityAdmin } from "./WebsiteCms";
import PrescriptionCms from "./PrescriptionCms";
import { formatDateDMY, formatSlotRange, parseBlockedDatesInput, roundInr } from "../../lib/dates";
import DmyDateInput from "../../components/DmyDateInput";

function useAdminApi() {
  const { getAuthHeaders } = useAuth();
  return {
    headers: () => getAuthHeaders(),
    get: (path) => axios.get(`${config.API}${path}`, { headers: getAuthHeaders() }),
    post: (path, body) => axios.post(`${config.API}${path}`, body, { headers: getAuthHeaders() }),
    patch: (path, body) => axios.patch(`${config.API}${path}`, body, { headers: getAuthHeaders() }),
    del: (path) => axios.delete(`${config.API}${path}`, { headers: getAuthHeaders() }),
    put: (path, body) => axios.put(`${config.API}${path}`, body, { headers: getAuthHeaders() }),
  };
}

const DEFAULT_CHARGES = [
  { id: "base", code: "base", label: "Product price", amount: 0, calc: "fixed", taxable: false },
  { id: "cgst", code: "cgst", label: "CGST", amount: 2.5, calc: "percent", taxable: false },
  { id: "sgst", code: "sgst", label: "SGST", amount: 2.5, calc: "percent", taxable: false },
];

function chargeCodeFromLabel(label, calc = "fixed", existing = "") {
  const kept = String(existing || "").trim().toLowerCase();
  if (kept && !["custom", "surcharge", "tax"].includes(kept)) return kept;
  const t = String(label || "").trim().toLowerCase();
  if (t.includes("cgst")) return "cgst";
  if (t.includes("sgst")) return "sgst";
  if (t.includes("igst")) return "igst";
  if (t === "gst" || t.startsWith("gst ") || t.startsWith("gst(") || /\bgst\b/.test(t)) return "gst";
  if (calc === "percent") return "tax";
  if (t === "base" || t === "product price" || t === "service / vaccine" || t === "service/vaccine") return "base";
  return kept || "surcharge";
}

function isBaseCharge(c) {
  const code = String(c?.code || c?.id || "").toLowerCase();
  if (code === "base") return true;
  const label = String(c?.label || "").trim().toLowerCase();
  return (c?.calc || "fixed") === "fixed" && /^(product price|service \/ vaccine|base)$/.test(label);
}

function mapChargeRows(rows, fallbackBase = 0) {
  const existing = Array.isArray(rows) && rows.length ? rows : [
    { ...DEFAULT_CHARGES[0], amount: fallbackBase || 0 },
    DEFAULT_CHARGES[1],
    DEFAULT_CHARGES[2],
  ];
  const mapped = existing.map((c, idx) => {
    const calc = c.calc === "percent" ? "percent" : "fixed";
    const label = c.label || "Charge";
    const code = chargeCodeFromLabel(label, calc, c.code || (idx === 0 ? "base" : ""));
    return {
      id: c.id || `chg-${idx + 1}`,
      code,
      label,
      amount: c.amount === "" || c.amount == null ? "" : c.amount,
      calc,
      taxable: calc === "percent" ? false : (c.taxable !== false),
      tax_percent: c.tax_percent ?? (calc === "percent" ? "" : 18),
    };
  });
  const extras = mapped.filter((c) => !isBaseCharge(c));
  const base = mapped.find(isBaseCharge) || { ...DEFAULT_CHARGES[0], amount: fallbackBase || 0 };
  if (fallbackBase !== "" && fallbackBase != null && Number(fallbackBase) >= 0) {
    base.amount = fallbackBase;
  }
  return [base, ...extras];
}

function serializeCharges(rows, baseAmount) {
  const extras = (rows || []).filter((c) => !isBaseCharge(c)).map((c, idx) => {
    const calc = c.calc === "percent" ? "percent" : "fixed";
    const label = (c.label || "").trim() || (calc === "percent" ? "Tax" : "Charge");
    return {
      id: c.id || `chg-${idx + 1}`,
      code: chargeCodeFromLabel(label, calc, c.code),
      label,
      calc,
      amount: Number(c.amount) || 0,
      taxable: calc === "percent" ? false : c.taxable !== false,
      tax_percent: calc === "percent" ? null : (Number(c.tax_percent) || 0),
    };
  });
  const amount = Number(baseAmount);
  return [
    {
      id: "base",
      code: "base",
      label: "Product price",
      calc: "fixed",
      amount: Number.isFinite(amount) && amount >= 0 ? amount : 0,
      taxable: false,
      tax_percent: 0,
    },
    ...extras,
  ];
}

function applyEditorToBrands(brands, selectedIds, editorCharges, editorPrice) {
  const payload = serializeCharges(editorCharges, editorPrice);
  const ids = new Set(selectedIds || []);
  return (brands || []).map((b) => (
    ids.has(b.id) ? { ...b, indicative_price: editorPrice, charges: payload } : b
  ));
}

function brandPayables(svc) {
  const brands = Array.isArray(svc?.brands) ? svc.brands.filter((b) => (typeof b === "string" ? b : b?.name)) : [];
  if (!brands.length) return [previewTotal(svc?.charges || [], productPrice(svc))];
  return brands.map((b) => {
    const row = typeof b === "string" ? { name: b } : b;
    const price = Number(row.indicative_price ?? productPrice(svc)) || 0;
    if (row.pricing?.total != null) return Number(row.pricing.total);
    return previewTotal(row.charges || svc.charges || [], price);
  });
}

function productPrice(svc) {
  const charges = svc?.charges || [];
  const base = charges.find((c) => (c.code || c.id) === "base" && (c.calc || "fixed") === "fixed");
  if (base != null && base.amount !== "" && base.amount != null) return Number(base.amount) || 0;
  const firstFixed = charges.find((c) => (c.calc || "fixed") === "fixed");
  if (firstFixed != null && firstFixed.amount !== "" && firstFixed.amount != null) {
    return Number(firstFixed.amount) || 0;
  }
  return Number(svc?.indicative_price) || 0;
}

function gstTotal(svc) {
  return previewTotal(svc?.charges || [], productPrice(svc));
}

function previewTotal(charges, fallbackBase = 0) {
  const rows = charges || [];
  const base = rows.find((c) => isBaseCharge(c) && (c.calc || "fixed") === "fixed");
  const product = Number(fallbackBase ?? base?.amount) || 0;
  let total = product;
  rows.forEach((c) => {
    if ((c.calc || "fixed") !== "percent") return;
    const code = String(c.code || "").toLowerCase();
    if (!["cgst", "sgst", "gst", "igst"].includes(code)) return;
    total += product * (Number(c.amount) || 0) / 100;
  });
  rows.forEach((c) => {
    const code = String(c.code || c.id || "").toLowerCase();
    if ((c.calc || "fixed") !== "fixed" || code === "base") return;
    const amt = Number(c.amount) || 0;
    if (amt <= 0) return;
    const tax = Number(c.tax_percent ?? (c.taxable === false ? 0 : 18)) || 0;
    total += amt + (amt * tax / 100);
  });
  return Math.round(total * 100) / 100;
}

function formatInr(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const rounded = roundInr(n);
  return `₹${Number(rounded).toLocaleString("en-IN")}`;
}

function formatProductInr(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

const VAX_CATEGORIES = [
  { id: "paediatric", label: "Paediatric" },
  { id: "adult", label: "Adult" },
  { id: "elderly", label: "Elderly" },
  { id: "travel", label: "Travel & Special" },
];

function emptyVaccine() {
  return {
    id: null,
    name: "",
    category: "adult",
    age_group: "",
    brands: [],
    description: "",
    indicative_price: "",
    active: true,
  };
}

export default function CmsAdmin() {
  const api = useAdminApi();
  const [tab, setTab] = useState("vaccines");
  const [services, setServices] = useState([]);
  const [articles, setArticles] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [badges, setBadges] = useState([]);
  const [msg, setMsg] = useState("");
  const [editService, setEditService] = useState(null);
  const [vaxForm, setVaxForm] = useState(null);
  const [charges, setCharges] = useState(DEFAULT_CHARGES);
  const [priceBrandId, setPriceBrandId] = useState("");
  const [slots, setSlots] = useState([]);
  const [bookable, setBookable] = useState({ weekdays: [0, 1, 2, 3, 4, 5, 6], blocked_dates: [] });
  const [blockedText, setBlockedText] = useState("");
  const [blockedDraft, setBlockedDraft] = useState("");
  const [blockedError, setBlockedError] = useState("");
  const [rules, setRules] = useState({ booking_lead_hours: 4, reschedule_allowed_window: 24 });
  const [coupons, setCoupons] = useState([]);
  const [couponForm, setCouponForm] = useState({ code: "", percent: 10, note: "" });
  const [servicePins, setServicePins] = useState([]);
  const [pinDraft, setPinDraft] = useState("");
  const [slotForm, setSlotForm] = useState({ hour: "09:00", end_hour: "10:00", label: formatSlotRange("09:00", "10:00"), capacity: 3, active: true });
  const [vaxCategories, setVaxCategories] = useState(VAX_CATEGORIES);
  const [globalCharges, setGlobalCharges] = useState([]);

  const reload = async () => {
    try {
      const [s, a, d, b, vs, bd, br, cp, cats, glob, pins] = await Promise.all([
        api.get("/admin/services"),
        api.get("/admin/articles"),
        api.get("/admin/doctors"),
        api.get("/admin/badges"),
        api.get("/admin/visit-slots"),
        axios.get(`${config.API}/config/bookable-days`),
        axios.get(`${config.API}/config/booking-rules`),
        api.get("/admin/coupons").catch(() => ({ data: { items: [] } })),
        api.get("/admin/config/vaccine-categories").catch(() => ({ data: { items: VAX_CATEGORIES } })),
        api.get("/admin/config/global-charges").catch(() => ({ data: { items: [] } })),
        axios.get(`${config.API}/config/service-pincodes`).catch(() => ({ data: { pincodes: [] } })),
      ]);
      setServices(s.data.items || []);
      setArticles(a.data.items || []);
      setDoctors(d.data.items || []);
      setBadges(b.data.items || []);
      setSlots(vs.data.items || []);
      setBookable({ weekdays: bd.data.weekdays || [], blocked_dates: bd.data.blocked_dates || [] });
      setBlockedText((bd.data.blocked_dates || []).map((d) => formatDateDMY(d)).join(", "));
      setBlockedError("");
      setRules({
        booking_lead_hours: br.data.booking_lead_hours ?? 4,
        reschedule_allowed_window: br.data.reschedule_allowed_window ?? 24,
      });
      setCoupons(cp.data.items || []);
      if (cats.data.items?.length) setVaxCategories(cats.data.items);
      setGlobalCharges(glob.data.items || []);
      setServicePins(pins.data.pincodes || []);
    } catch (e) {
      setMsg(e.response?.data?.detail || "Failed to load");
    }
  };

  useEffect(() => { reload(); }, []);

  const seed = async () => {
    await api.post("/admin/seed-catalogue?force=true", {});
    setMsg("Catalogue + visit slots seeded from Mongo");
    reload();
  };

  const openVaccine = (svc) => {
    const row = svc || emptyVaccine();
    const serviceCharges = mapChargeRows(row.charges, productPrice(row) || row.indicative_price || 0);
    const brands = Array.isArray(row.brands)
      ? row.brands.map((b, i) => {
        if (typeof b === "string") {
          return {
            id: `brand-${i + 1}`,
            name: b,
            indicative_price: row.indicative_price || "",
            charges: serializeCharges(serviceCharges, row.indicative_price),
          };
        }
        const price = b.indicative_price ?? row.indicative_price ?? "";
        return {
          id: b.id || `brand-${i + 1}`,
          name: b.name || "",
          indicative_price: price,
          charges: Array.isArray(b.charges) && b.charges.length
            ? serializeCharges(mapChargeRows(b.charges, price), price)
            : serializeCharges(serviceCharges, price),
        };
      })
      : [];
    const first = brands[0];
    setVaxForm({
      id: row.id || null,
      name: row.name || "",
      category: row.category === "adults" ? "adult" : (row.category || "adult"),
      age_group: row.age_group || row.ageGroup || "",
      brands,
      description: row.description || "",
      indicative_price: first?.indicative_price ?? productPrice(row) ?? row.indicative_price ?? "",
      active: row.active !== false,
    });
    setPriceBrandId(first ? first.id : "");
    setCharges(first ? mapChargeRows(first.charges, first.indicative_price) : serviceCharges);
    setEditService(row.id ? row : { ...row, charges: DEFAULT_CHARGES, indicative_price: 0, name: "New vaccine" });
  };

  const closeVaccine = () => {
    setEditService(null);
    setVaxForm(null);
    setPriceBrandId("");
  };

  const persistEditorToForm = (form = vaxForm, selectedId = priceBrandId, editorCharges = charges) => {
    if (!form) return form;
    if (!(form.brands || []).length || !selectedId) return form;
    return { ...form, brands: applyEditorToBrands(form.brands, [selectedId], editorCharges, form.indicative_price) };
  };

  const selectPriceBrand = (id) => {
    if (!vaxForm || !id || id === priceBrandId) return;
    const flushed = persistEditorToForm();
    const brand = (flushed.brands || []).find((b) => b.id === id);
    setPriceBrandId(id);
    setCharges(mapChargeRows(brand?.charges, brand?.indicative_price));
    setVaxForm({ ...flushed, indicative_price: brand?.indicative_price ?? "" });
  };

  const patchCharge = (idx, patch) => {
    setCharges((prev) => {
      const next = [...prev];
      const cur = { ...next[idx], ...patch };
      if (patch.label != null || patch.calc != null) {
        cur.code = chargeCodeFromLabel(cur.label, cur.calc, patch.code || cur.code);
      }
      next[idx] = cur;
      return next;
    });
  };

  const addChargeRow = (preset) => {
    const row = preset || {
      id: `chg-${Date.now()}`,
      code: "surcharge",
      label: "Surcharge",
      amount: 0,
      calc: "fixed",
      taxable: true,
    };
    setCharges((prev) => [...prev, {
      ...row,
      id: row.id || `chg-${Date.now()}`,
      code: chargeCodeFromLabel(row.label, row.calc, row.code),
    }]);
  };

  const saveCharges = async () => {
    if (!editService && !vaxForm) return;
    const name = (vaxForm?.name || editService?.name || "").trim();
    if (!name) {
      setMsg("Vaccine name is required");
      return;
    }
    const flushed = persistEditorToForm();
    const payload = serializeCharges(charges, flushed?.indicative_price);
    const baseAmount = Number(flushed?.indicative_price);
    const brands = (Array.isArray(flushed?.brands) ? flushed.brands : [])
      .map((b, i) => ({
        id: b.id || `brand-${i + 1}`,
        name: String(b.name || "").trim(),
        indicative_price: Number(b.indicative_price) || Number(flushed?.indicative_price) || 0,
        charges: Array.isArray(b.charges) && b.charges.length
          ? serializeCharges(mapChargeRows(b.charges, b.indicative_price), b.indicative_price)
          : payload,
      }))
      .filter((b) => b.name);
    if (brands.length && !priceBrandId) {
      setMsg("Select a brand to set its price and GST.");
      return;
    }
    const fields = {
      type: "vaccination",
      name,
      category: vaxForm?.category || "adult",
      age_group: (vaxForm?.age_group || "").trim(),
      brands,
      description: (vaxForm?.description || "").trim(),
      indicative_price: brands[0]
        ? Number(brands[0].indicative_price) || 0
        : (Number.isFinite(baseAmount) ? baseAmount : productPrice({ charges: payload })),
      active: vaxForm ? vaxForm.active !== false : editService?.active !== false,
    };
    try {
      let id = vaxForm?.id || editService?.id;
      if (!id) {
        const created = await api.post("/admin/services", fields);
        id = created.data.id;
      } else {
        await api.patch(`/admin/services/${id}`, fields);
      }
      const { data } = await api.put(`/admin/services/${id}/charges`, brands[0]?.charges || payload);
      setMsg(`${name} saved — it now shows on the public vaccine catalogue.`);
      closeVaccine();
      reload();
      return data;
    } catch (e) {
      setMsg(e.response?.data?.detail || "Failed to save vaccine");
    }
  };

  const saveInlinePrice = async (svc, raw) => {
    const next = Number(raw);
    if (Number.isNaN(next) || next < 0) return;
    const current = productPrice(svc);
    if (next === current) return;
    try {
      const { data } = await api.patch(`/admin/services/${svc.id}`, { indicative_price: next });
      setServices((prev) => prev.map((s) => (s.id === svc.id ? { ...s, ...data } : s)));
      setMsg(`Price updated for ${svc.name}`);
    } catch (e) {
      setMsg(e.response?.data?.detail || "Failed to update price");
    }
  };

  return (
    <div data-testid="cms-admin">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-[#311b52]" style={{ fontFamily: "Poppins" }}>Content & Catalogue CMS</h1>
        <button onClick={seed} className="bg-[#f37721] text-white px-4 py-2 rounded-md text-sm font-medium">Seed / refresh catalogue + slots</button>
      </div>
      {msg && <p className="text-sm text-[#673c96] mb-4">{msg}</p>}
      <div className="flex flex-wrap gap-2 mb-6">
        {["website", "prescription", "vaccines", "slots & days", "alerts", "rules & coupons", "articles", "doctors", "badges"].map((t) => (
          <button key={t} onClick={() => { closeVaccine(); setTab(t); }}
            className={`px-3 py-1.5 rounded-md text-sm ${tab === t ? "bg-[#311b52] text-white" : "bg-white border"}`}>{t}</button>
        ))}
      </div>

      {tab === "website" && <WebsiteCms key="website-cms" onMsg={setMsg} />}
      {tab === "prescription" && <PrescriptionCms key="prescription-cms" onMsg={setMsg} />}
      {tab === "alerts" && <AlertRecipients key="alerts" onMsg={setMsg} />}
      {tab === "slots & days" && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-[#673c96]/10 p-4">
            <h2 className="font-semibold text-[#311b52] mb-1">Bookable days</h2>
            <p className="text-xs text-[#6b7280] mb-3">Customers can only book — and nurses can only set availability — on these weekdays. Add specific blocked dates (holidays) below.</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((w, idx) => {
                const on = bookable.weekdays.includes(idx);
                return (
                  <button key={w} type="button"
                    onClick={() => setBookable((prev) => ({
                      ...prev,
                      weekdays: on ? prev.weekdays.filter((x) => x !== idx) : [...prev.weekdays, idx].sort(),
                    }))}
                    className={`px-4 py-2 rounded-full text-sm font-semibold border ${on ? "bg-[#673c96] text-white border-[#673c96]" : "border-[#673c96]/25 text-[#311b52] bg-white"}`}
                    data-testid={`bookable-day-${idx}`}>
                    {w}
                  </button>
                );
              })}
            </div>
            <label className="text-xs font-medium text-[#311b52] block mb-1">Blocked dates (comma separated, DD-MM-YYYY only)</label>
            <div className="flex flex-wrap gap-2 mb-2">
              <div className="flex-1 min-w-[160px]">
                <DmyDateInput value={blockedDraft} onChange={setBlockedDraft} className="w-full px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm" data-testid="blocked-date-picker" />
              </div>
              <button type="button"
                onClick={() => {
                  if (!blockedDraft) {
                    setBlockedError("Pick a valid date first (DD-MM-YYYY).");
                    return;
                  }
                  const next = blockedText ? `${blockedText}, ${formatDateDMY(blockedDraft)}` : formatDateDMY(blockedDraft);
                  const parsed = parseBlockedDatesInput(next);
                  setBlockedText(parsed.validIso.map((iso) => formatDateDMY(iso)).join(", "));
                  setBlockedDraft("");
                  setBlockedError("");
                }}
                className="px-3 py-2 rounded-lg border border-[#673c96]/30 text-sm font-semibold text-[#673c96]">
                Add date
              </button>
            </div>
            <input
              value={blockedText}
              onChange={(e) => {
                const next = e.target.value.replace(/[^\d,\-\s]/g, "");
                setBlockedText(next);
                const parsed = parseBlockedDatesInput(next);
                setBlockedError(parsed.invalid.length
                  ? `Use dates only as DD-MM-YYYY, separated by commas. Invalid: ${parsed.invalid.join(", ")}`
                  : "");
              }}
              placeholder="02-10-2026, 01-11-2026"
              className={`w-full px-3 py-2 rounded-lg border text-sm ${blockedError ? "border-[#ee4036]" : "border-[#673c96]/20"}`}
              data-testid="blocked-dates-input" />
            {blockedError ? <p className="text-xs text-[#ee4036] mt-1">{blockedError}</p> : (
              <p className="text-xs text-[#6b7280] mt-1">Example: 02-10-2026, 01-11-2026. Letters and other formats are not allowed.</p>
            )}
            {blockedText && !blockedError && (
              <div className="flex flex-wrap gap-1 mt-2">
                {parseBlockedDatesInput(blockedText).validIso.map((iso) => (
                  <span key={iso} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#f3eef8] text-xs text-[#311b52]">
                    {formatDateDMY(iso)}
                    <button type="button" className="text-[#ee4036]" aria-label={`Remove ${formatDateDMY(iso)}`}
                      onClick={() => {
                        const next = parseBlockedDatesInput(blockedText).validIso
                          .filter((d) => d !== iso)
                          .map((d) => formatDateDMY(d))
                          .join(", ");
                        setBlockedText(next);
                      }}>×</button>
                  </span>
                ))}
              </div>
            )}
            <button type="button"
              onClick={async () => {
                const parsed = parseBlockedDatesInput(blockedText);
                if (parsed.invalid.length) {
                  setBlockedError(`Use dates only as DD-MM-YYYY, separated by commas. Invalid: ${parsed.invalid.join(", ")}`);
                  setMsg("Blocked dates must be valid DD-MM-YYYY values, comma separated.");
                  return;
                }
                try {
                  await api.put("/admin/config/bookable-days", { weekdays: bookable.weekdays, blocked_dates: parsed.validIso });
                  setBlockedText(parsed.validIso.map((iso) => formatDateDMY(iso)).join(", "));
                  setBlockedError("");
                  setMsg("Bookable days saved");
                } catch (e) {
                  setMsg(e.response?.data?.detail || "Failed to save bookable days");
                }
              }}
              className="mt-3 bg-[#673c96] text-white px-4 py-2 rounded-md text-sm font-medium" data-testid="save-bookable-days">
              Save bookable days
            </button>
          </div>

          <div className="bg-white rounded-xl border border-[#673c96]/10 p-4">
            <h2 className="font-semibold text-[#311b52] mb-3">Visit slots</h2>
            <p className="text-xs text-[#6b7280] mb-3">Each slot has a start and end time so visits can be 30 minutes, 90 minutes, or any other duration.</p>
            <form className="grid sm:grid-cols-5 gap-2 mb-4" onSubmit={async (e) => {
              e.preventDefault();
              try {
                await api.post("/admin/visit-slots", { ...slotForm, label: slotForm.label || formatSlotRange(slotForm.hour, slotForm.end_hour) });
                setSlotForm({ hour: "09:00", end_hour: "10:00", label: formatSlotRange("09:00", "10:00"), capacity: 3, active: true });
                setMsg("Slot added");
                reload();
              } catch (err) {
                setMsg(err.response?.data?.detail || "Could not add slot");
              }
            }}>
              <input required type="time" value={slotForm.hour} onChange={(e) => setSlotForm({ ...slotForm, hour: e.target.value, label: formatSlotRange(e.target.value, slotForm.end_hour) })} className="px-3 py-2 rounded-lg border text-sm" title="Start" />
              <input required type="time" value={slotForm.end_hour} onChange={(e) => setSlotForm({ ...slotForm, end_hour: e.target.value, label: formatSlotRange(slotForm.hour, e.target.value) })} className="px-3 py-2 rounded-lg border text-sm" title="End" />
              <input required value={slotForm.label} onChange={(e) => setSlotForm({ ...slotForm, label: e.target.value })} placeholder="9:00 AM – 10:00 AM" className="px-3 py-2 rounded-lg border text-sm" />
              <input required type="number" min={0} max={50} value={slotForm.capacity} onChange={(e) => setSlotForm({ ...slotForm, capacity: Number(e.target.value) })} className="px-3 py-2 rounded-lg border text-sm" />
              <button type="submit" className="bg-[#f37721] text-white rounded-md text-sm font-semibold">Add slot</button>
            </form>
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[#673c96]">
                    {["start", "end", "window", "capacity", "active", ""].map((c) => <th key={c} className="py-2 pr-3">{c}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {slots.map((s) => (
                    <tr key={s.id} className="border-t border-[#673c96]/10">
                      <td className="py-2 pr-3 text-[#311b52] font-mono text-xs">{s.hour}</td>
                      <td className="py-2 pr-3">
                        <input type="time" defaultValue={s.end_hour || ""} key={`${s.id}-end`}
                          onBlur={async (e) => {
                            const v = e.target.value;
                            if (v && v !== s.end_hour) {
                              await api.patch(`/admin/visit-slots/${s.id}`, { end_hour: v, label: formatSlotRange(s.hour, v) });
                              setMsg(`End time updated for ${s.hour}`);
                              reload();
                            }
                          }}
                          className="px-2 py-1 rounded border border-[#673c96]/20 text-sm" />
                      </td>
                      <td className="py-2 pr-3">
                        <input defaultValue={s.label} key={`${s.id}-label`}
                          onBlur={async (e) => {
                            const v = e.target.value.trim();
                            if (v && v !== s.label) {
                              await api.patch(`/admin/visit-slots/${s.id}`, { label: v });
                              setMsg(`Label updated for ${s.hour}`);
                              reload();
                            }
                          }}
                          className="w-36 px-2 py-1 rounded border border-[#673c96]/20 text-sm" />
                      </td>
                      <td className="py-2 pr-3">
                        <input type="number" min={0} max={50} defaultValue={s.capacity}
                          onBlur={async (e) => {
                            const v = parseInt(e.target.value, 10);
                            if (!Number.isNaN(v) && v !== s.capacity) {
                              await api.patch(`/admin/visit-slots/${s.id}`, { capacity: v });
                              setMsg(`Capacity updated for ${s.hour}`);
                            }
                          }}
                          className="w-20 px-2 py-1 rounded border border-[#673c96]/20 text-sm" />
                      </td>
                      <td className="py-2 pr-3">
                        <button type="button"
                          onClick={async () => { await api.patch(`/admin/visit-slots/${s.id}`, { active: !s.active }); reload(); }}
                          className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${s.active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                          {s.active ? "active" : "off"}
                        </button>
                      </td>
                      <td className="py-2 pr-3">
                        <button type="button" className="text-[#ee4036] text-xs font-semibold"
                          onClick={async () => {
                            if (!window.confirm(`Remove slot ${s.label || s.hour}?`)) return;
                            await api.del(`/admin/visit-slots/${s.id}`);
                            setMsg("Slot removed");
                            reload();
                          }}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                  {slots.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-[#673c96]">No slots — add one above or click "Seed / refresh catalogue + slots".</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
          <NurseAvailabilityAdmin onMsg={setMsg} />
        </div>
      )}

      {tab === "rules & coupons" && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-[#673c96]/10 p-4 space-y-3">
            <h2 className="font-semibold text-[#311b52]">Booking rules</h2>
            <p className="text-xs text-[#6b7280]">Integer hours. Lead time is the earliest a customer can book from now. Reschedule window is how many hours before the visit self-reschedule is still allowed.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="text-sm text-[#311b52]">
                Booking lead hours
                <input type="number" min={0} value={rules.booking_lead_hours}
                  onChange={(e) => setRules((r) => ({ ...r, booking_lead_hours: Number(e.target.value) }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm" data-testid="rule-lead-hours" />
              </label>
              <label className="text-sm text-[#311b52]">
                Reschedule allowed window (hours)
                <input type="number" min={0} value={rules.reschedule_allowed_window}
                  onChange={(e) => setRules((r) => ({ ...r, reschedule_allowed_window: Number(e.target.value) }))}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-[#673c96]/20 text-sm" data-testid="rule-reschedule-window" />
              </label>
            </div>
            <button type="button"
              onClick={async () => {
                try {
                  await api.put("/admin/config/booking-rules", rules);
                  setMsg("Booking rules saved");
                } catch (e) {
                  setMsg(e.response?.data?.detail || "Failed to save rules");
                }
              }}
              className="bg-[#673c96] text-white px-4 py-2 rounded-md text-sm font-medium">
              Save rules
            </button>
          </div>

          <div className="bg-white rounded-xl border border-[#673c96]/10 p-4 space-y-3">
            <h2 className="font-semibold text-[#311b52]">Service PIN codes</h2>
            <p className="text-xs text-[#6b7280]">Appointments are accepted only for these PIN codes. BookMyJab is currently operational in Gurugram — add or remove PIN codes as you expand.</p>
            <form
              className="flex flex-wrap gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                const pin = String(pinDraft || "").replace(/\D/g, "").slice(0, 6);
                if (!/^\d{6}$/.test(pin)) {
                  setMsg("Enter a valid 6-digit PIN code");
                  return;
                }
                if (servicePins.includes(pin)) {
                  setMsg("That PIN code is already in the list");
                  return;
                }
                setServicePins((list) => [...list, pin].sort());
                setPinDraft("");
                setMsg("");
              }}
            >
              <input
                value={pinDraft}
                onChange={(e) => setPinDraft(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="122001"
                maxLength={6}
                inputMode="numeric"
                className="px-3 py-2 rounded-lg border text-sm w-32"
                data-testid="service-pin-input"
              />
              <button type="submit" className="bg-[#f37721] text-white rounded-md text-sm font-semibold px-4">Add PIN</button>
            </form>
            <div className="flex flex-wrap gap-2">
              {servicePins.map((pin) => (
                <span key={pin} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#fdfcff] border border-[#673c96]/20 text-sm text-[#311b52]">
                  {pin}
                  <button
                    type="button"
                    className="text-[#ee4036] text-xs font-semibold"
                    onClick={() => setServicePins((list) => list.filter((p) => p !== pin))}
                  >
                    Remove
                  </button>
                </span>
              ))}
              {servicePins.length === 0 && <p className="text-sm text-[#673c96]">No PIN codes yet. Add Gurugram PIN codes to start accepting bookings.</p>}
            </div>
            <button
              type="button"
              onClick={async () => {
                try {
                  const { data } = await api.put("/admin/config/service-pincodes", { pincodes: servicePins });
                  setServicePins(data.pincodes || servicePins);
                  setMsg("Service PIN codes saved");
                } catch (e) {
                  setMsg(e.response?.data?.detail || "Failed to save PIN codes");
                }
              }}
              className="bg-[#673c96] text-white px-4 py-2 rounded-md text-sm font-medium"
              data-testid="save-service-pins"
            >
              Save PIN codes
            </button>
          </div>

          <div className="bg-white rounded-xl border border-[#673c96]/10 p-4">
            <h2 className="font-semibold text-[#311b52] mb-3">Coupon codes (percentage off)</h2>
            <form className="grid sm:grid-cols-4 gap-2 mb-4" onSubmit={async (e) => {
              e.preventDefault();
              try {
                await api.post("/admin/coupons", couponForm);
                setCouponForm({ code: "", percent: 10, note: "" });
                setMsg("Coupon created");
                reload();
              } catch (err) {
                setMsg(err.response?.data?.detail || "Could not create coupon");
              }
            }}>
              <input required value={couponForm.code} onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })} placeholder="CODE" className="px-3 py-2 rounded-lg border text-sm" />
              <input required type="number" min={1} max={100} value={couponForm.percent} onChange={(e) => setCouponForm({ ...couponForm, percent: Number(e.target.value) })} placeholder="%" className="px-3 py-2 rounded-lg border text-sm" />
              <input value={couponForm.note} onChange={(e) => setCouponForm({ ...couponForm, note: e.target.value })} placeholder="Note" className="px-3 py-2 rounded-lg border text-sm" />
              <button type="submit" className="bg-[#f37721] text-white rounded-md text-sm font-semibold">Add coupon</button>
            </form>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[#673c96]">
                  {["code", "percent", "note", "active", "actions"].map((c) => <th key={c} className="py-2 pr-3">{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {coupons.map((c) => (
                  <tr key={c.id} className="border-t">
                    <td className="py-2 pr-3 font-mono">{c.code}</td>
                    <td className="py-2 pr-3">
                      <input type="number" min={1} max={100} defaultValue={c.percent} key={`${c.id}-${c.percent}`}
                        onBlur={async (e) => {
                          const v = Number(e.target.value);
                          if (v && v !== c.percent) { await api.patch(`/admin/coupons/${c.id}`, { percent: v }); reload(); }
                        }}
                        className="w-20 px-2 py-1 rounded border text-sm" />
                    </td>
                    <td className="py-2 pr-3 text-[#6b7280]">{c.note || "—"}</td>
                    <td className="py-2 pr-3">{c.active ? "active" : "off"}</td>
                    <td className="py-2 pr-3">
                      <button type="button" className="text-[#f37721] text-xs font-semibold"
                        onClick={async () => { await api.patch(`/admin/coupons/${c.id}`, { active: !c.active }); reload(); }}>
                        {c.active ? "Disable" : "Enable"}
                      </button>
                    </td>
                  </tr>
                ))}
                {coupons.length === 0 && <tr><td colSpan={5} className="py-4 text-[#673c96]">No coupons yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "vaccines" && (
        <div className="space-y-4" data-testid="vaccines-admin">
          <div className="bg-white rounded-xl border border-[#673c96]/10 p-4 space-y-3">
            <h2 className="font-semibold text-[#311b52]">Vaccine categories</h2>
            <p className="text-xs text-[#6b7280]">These tabs appear on the public catalogue. Add, rename or remove — more than four is allowed.</p>
            {(vaxCategories || []).map((c, idx) => (
              <div key={c.id || idx} className="flex gap-2">
                <input className="flex-1 border rounded-lg px-3 py-2 text-sm" value={c.label}
                  onChange={(e) => setVaxCategories((prev) => prev.map((x, i) => i === idx ? { ...x, label: e.target.value } : x))} />
                <button type="button" className="text-[#ee4036] text-sm" onClick={() => setVaxCategories((prev) => prev.filter((_, i) => i !== idx))}>Remove</button>
              </div>
            ))}
            <button type="button" className="text-sm font-semibold text-[#673c96]"
              onClick={() => setVaxCategories((prev) => [...prev, { id: "", label: "New category" }])}>+ Add category</button>
            <button type="button" className="ml-3 bg-[#673c96] text-white px-3 py-1.5 rounded-md text-sm"
              onClick={async () => {
                try {
                  const { data } = await api.put("/admin/config/vaccine-categories", { items: vaxCategories });
                  setVaxCategories(data.items || vaxCategories);
                  setMsg("Categories saved");
                } catch (e) { setMsg(e.response?.data?.detail || "Could not save categories"); }
              }}>Save categories</button>
          </div>

          <div className="bg-white rounded-xl border border-[#673c96]/10 p-4 space-y-3">
            <h2 className="font-semibold text-[#311b52]">Global charges</h2>
            <p className="text-xs text-[#6b7280]">Auto-applied on every booking. Customers can remove a line at checkout if it is marked removable.</p>
            {globalCharges.map((c, idx) => (
              <div key={c.id || idx} className="grid grid-cols-12 gap-2 items-center">
                <input className="col-span-4 border rounded-lg px-2 py-1.5 text-sm" value={c.label || ""} placeholder="e.g. Administration"
                  onChange={(e) => setGlobalCharges((prev) => prev.map((x, i) => i === idx ? { ...x, label: e.target.value } : x))} />
                <select className="col-span-2 border rounded-lg px-2 py-1.5 text-sm" value={c.calc || "fixed"}
                  onChange={(e) => setGlobalCharges((prev) => prev.map((x, i) => i === idx ? { ...x, calc: e.target.value } : x))}>
                  <option value="fixed">Fixed ₹</option>
                  <option value="percent">Percent %</option>
                </select>
                <input type="number" className="col-span-2 border rounded-lg px-2 py-1.5 text-sm" value={c.amount ?? ""}
                  onChange={(e) => setGlobalCharges((prev) => prev.map((x, i) => i === idx ? { ...x, amount: e.target.value } : x))} />
                <label className="col-span-2 text-[11px] flex items-center gap-1">
                  <input type="checkbox" checked={c.taxable !== false}
                    onChange={(e) => setGlobalCharges((prev) => prev.map((x, i) => i === idx ? { ...x, taxable: e.target.checked } : x))} /> Taxed
                </label>
                <label className="col-span-1 text-[11px] flex items-center gap-1">
                  <input type="checkbox" checked={c.removable !== false}
                    onChange={(e) => setGlobalCharges((prev) => prev.map((x, i) => i === idx ? { ...x, removable: e.target.checked } : x))} /> Removable
                </label>
                <button type="button" className="col-span-1 text-[#ee4036]" onClick={() => setGlobalCharges((prev) => prev.filter((_, i) => i !== idx))}>×</button>
              </div>
            ))}
            <button type="button" className="text-sm font-semibold text-[#673c96]"
              onClick={() => setGlobalCharges((prev) => [...prev, { id: `global-${Date.now()}`, code: "surcharge", label: "Administration", amount: 0, calc: "fixed", taxable: true, removable: true, auto_apply: true }])}>
              + Add global charge
            </button>
            <button type="button" className="ml-3 bg-[#673c96] text-white px-3 py-1.5 rounded-md text-sm"
              onClick={async () => {
                try {
                  const items = globalCharges.map((c, i) => ({
                    ...c,
                    id: c.id || `global-${i + 1}`,
                    amount: Number(c.amount) || 0,
                    calc: c.calc === "percent" ? "percent" : "fixed",
                    taxable: c.taxable !== false,
                    removable: c.removable !== false,
                    auto_apply: true,
                  }));
                  const { data } = await api.put("/admin/config/global-charges", { items });
                  setGlobalCharges(data.items || items);
                  setMsg("Global charges saved — they apply on new bookings");
                } catch (e) { setMsg(e.response?.data?.detail || "Could not save global charges"); }
              }}>Save global charges</button>
          </div>

        <div className="bg-white rounded-xl border border-[#673c96]/10 p-4">
          <div className="flex flex-wrap justify-between gap-3 mb-2">
            <div>
              <h2 className="font-semibold text-[#311b52]">Vaccines</h2>
              <p className="text-xs text-[#6b7280] mt-1">This is the catalogue customers see. Download the live inventory CSV, edit brands and GST in Excel, then use Replace all to swap the catalogue — the file is validated first, and nothing is wiped if a row is invalid.</p>
              <p className="text-xs text-[#673c96] mt-1">To add brand names: <strong>Edit</strong> → <strong>+ Add brand</strong>. Select a brand under <strong>Change price &amp; GST for</strong> to set that brand’s product price and tax independently.</p>
            </div>
            <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => openVaccine(null)} className="text-sm bg-[#673c96] text-white px-3 py-1.5 rounded-md h-fit">Add vaccine</button>
            <button type="button" className="text-sm border border-[#673c96]/30 text-[#673c96] px-3 py-1.5 rounded-md h-fit" onClick={async () => {
              try {
                const { data } = await axios.get(`${config.API}/admin/services/export`, { headers: api.headers(), responseType: "blob" });
                const url = URL.createObjectURL(data);
                const a = document.createElement("a");
                a.href = url;
                a.download = "bookmyjab-vaccines-export.csv";
                a.click();
                URL.revokeObjectURL(url);
              } catch (err) {
                setMsg(err.response?.data?.detail || "Could not download inventory");
              }
            }}>Download all vaccines CSV</button>
            <button type="button" className="text-sm border border-[#673c96]/30 text-[#673c96] px-3 py-1.5 rounded-md h-fit" onClick={async () => {
              try {
                const { data } = await axios.get(`${config.API}/admin/services/import-template`, { headers: api.headers(), responseType: "blob" });
                const url = URL.createObjectURL(data);
                const a = document.createElement("a");
                a.href = url;
                a.download = "bookmyjab-vaccines-template.csv";
                a.click();
                URL.revokeObjectURL(url);
              } catch (err) {
                setMsg(err.response?.data?.detail || "Could not download template");
              }
            }}>Download CSV template</button>
            <label className="text-sm border border-[#673c96]/30 text-[#673c96] px-3 py-1.5 rounded-md h-fit cursor-pointer">
              Import CSV (add/update)
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (!file) return;
                const fd = new FormData();
                fd.append("file", file);
                try {
                  const { data } = await axios.post(`${config.API}/admin/services/import`, fd, { headers: api.headers() });
                  setMsg(`Imported ${data.created || 0} new, updated ${data.updated || 0}${data.errors?.length ? `. Issues: ${data.errors.slice(0, 3).join("; ")}` : ""}`);
                  reload();
                } catch (err) {
                  setMsg(err.response?.data?.detail || "Could not import file");
                }
              }} />
            </label>
            <label className="text-sm border border-[#ee4036]/40 text-[#ee4036] px-3 py-1.5 rounded-md h-fit cursor-pointer">
              Replace all from CSV
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (!file) return;
                if (!window.confirm("Replace ALL vaccines with this CSV? Existing vaccination catalogue rows will be deleted only after the file passes validation.")) return;
                const fd = new FormData();
                fd.append("file", file);
                try {
                  const { data } = await axios.post(`${config.API}/admin/services/import-replace`, fd, { headers: api.headers() });
                  setMsg(`Replaced catalogue with ${data.replaced || 0} vaccines`);
                  reload();
                } catch (err) {
                  const d = err.response?.data?.detail;
                  const extra = d && typeof d === "object" ? `${d.message || "Validation failed"}${(d.errors || []).length ? `: ${d.errors.slice(0, 5).join("; ")}` : ""}` : (d || "Could not replace catalogue");
                  setMsg(extra);
                }
              }} />
            </label>
            <button type="button" className="text-sm border border-emerald-300 text-emerald-800 px-3 py-1.5 rounded-md h-fit" onClick={async () => {
              try {
                const { data } = await api.post("/admin/services/activate-all", {});
                setMsg(`All vaccines are now visible (${data.updated || 0} updated)`);
                reload();
              } catch (err) {
                setMsg(err.response?.data?.detail || "Could not show all vaccines");
              }
            }}>Show all on catalogue</button>
            </div>
          </div>
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[#673c96]">
                  {["name", "brands", "category", "age", "price (without GST)", "customer pays", "on site", "actions"].map((c) => <th key={c} className="py-2 pr-3">{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {services.filter((r) => (r.type || "vaccination") === "vaccination").map((r) => (
                  <tr key={r.id} className="border-t border-[#673c96]/10">
                    <td className="py-2 pr-3 text-[#311b52] font-medium">{r.name}</td>
                    <td className="py-2 pr-3 text-[#6b7280]">{
                      (Array.isArray(r.brands) ? r.brands : [])
                        .map((b) => (typeof b === "string" ? b : b.name))
                        .filter(Boolean)
                        .join(", ") || "—"
                    }</td>
                    <td className="py-2 pr-3 text-[#311b52] capitalize">{r.category || "—"}</td>
                    <td className="py-2 pr-3 text-[#6b7280]">{r.age_group || "—"}</td>
                    <td className="py-2 pr-3 text-[#311b52]">
                      {(Array.isArray(r.brands) && r.brands.length) ? (
                        <span className="text-xs">{
                          [...new Set(r.brands.map((b) => formatProductInr(typeof b === "string" ? productPrice(r) : (b.indicative_price ?? productPrice(r)))))].join(" / ")
                        }</span>
                      ) : (
                        <input type="number" min={0} step="1" defaultValue={productPrice(r) || ""}
                          key={`${r.id}-${productPrice(r)}`}
                          onBlur={(e) => saveInlinePrice(r, e.target.value)}
                          className="w-24 px-2 py-1 rounded border border-[#673c96]/20 text-sm"
                          data-testid={`svc-price-${r.id}`} />
                      )}
                    </td>
                    <td className="py-2 pr-3 text-[#311b52] font-semibold">{
                      (Array.isArray(r.brands) && r.brands.length)
                        ? [...new Set(brandPayables(r).map((n) => formatInr(n)))].join(" / ")
                        : formatInr(r.pricing?.total ?? gstTotal(r))
                    }</td>
                    <td className="py-2 pr-3">
                      <button type="button"
                        onClick={async () => {
                          await api.patch(`/admin/services/${r.id}`, { active: r.active === false });
                          setMsg(r.active === false ? `${r.name} is now on the public catalogue` : `${r.name} hidden from the public catalogue`);
                          reload();
                        }}
                        className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${r.active === false ? "bg-gray-100 text-gray-500" : "bg-emerald-100 text-emerald-700"}`}>
                        {r.active === false ? "hidden" : "visible"}
                      </button>
                    </td>
                    <td className="py-2 pr-3 whitespace-nowrap">
                      <button type="button" onClick={() => openVaccine(r)} className="text-[#f37721] font-semibold text-xs mr-3">Edit</button>
                      <button type="button" className="text-[#ee4036] font-semibold text-xs"
                        onClick={async () => {
                          if (!window.confirm(`Delete ${r.name}? Customers will no longer see it.`)) return;
                          await api.del(`/admin/services/${r.id}`);
                          setMsg(`${r.name} deleted`);
                          reload();
                        }}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {services.filter((r) => (r.type || "vaccination") === "vaccination").length === 0 && (
              <p className="text-[#673c96] text-sm py-4">No vaccines yet. Click Add vaccine or Seed / refresh catalogue + slots.</p>
            )}
          </div>
        </div>
        </div>
      )}
      {tab === "articles" && (
        <Panel key="articles" title="Articles" onAdd={async () => {
          await api.post("/admin/articles", { title: "New article", body: "Draft body", excerpt: "", published: true });
          reload();
        }} rows={articles} columns={["title", "slug", "published"]} />
      )}
      {tab === "doctors" && (
        <Panel key="doctors" title="Doctors" onAdd={async () => {
          await api.post("/admin/doctors", { full_name: "Dr. New", specialty: "General", active: true });
          reload();
        }} rows={doctors} columns={["full_name", "specialty", "active"]} />
      )}
      {tab === "badges" && (
        <Panel key="badges" title="Vaccination badges" onAdd={async () => {
          await api.post("/admin/badges", { name: "New badge", description: "" });
          reload();
        }} rows={badges} columns={["name", "active"]} />
      )}

      {tab === "vaccines" && (editService || vaxForm) && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto" data-testid="vaccine-editor">
            <h3 className="text-lg font-bold text-[#311b52]">{vaxForm?.id ? "Edit vaccine" : "Add vaccine"}</h3>
            {vaxForm && (
              <div className="space-y-3">
                <label className="block text-sm text-[#311b52]">
                  Name *
                  <input className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" value={vaxForm.name}
                    onChange={(e) => setVaxForm({ ...vaxForm, name: e.target.value })} data-testid="vax-name" />
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block text-sm text-[#311b52]">
                    Category
                    <select className="mt-1 w-full border rounded-lg px-3 py-2 text-sm bg-white" value={vaxForm.category}
                      onChange={(e) => setVaxForm({ ...vaxForm, category: e.target.value })}>
                      {vaxCategories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                  </label>
                  <label className="block text-sm text-[#311b52]">
                    Age group
                    <input className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" value={vaxForm.age_group}
                      onChange={(e) => setVaxForm({ ...vaxForm, age_group: e.target.value })} placeholder="e.g. Adults, 9 months+" />
                  </label>
                </div>
                <div className="rounded-xl border border-[#673c96]/15 bg-[#faf7ff] p-3 space-y-2" data-testid="vax-brands">
                  <p className="text-sm text-[#311b52] font-semibold">Brands for this vaccine type</p>
                  <p className="text-xs text-[#6b7280]">Add each brand name here. Then select one brand below to set its own product price and GST — they stay independent.</p>
                  {(vaxForm.brands || []).map((b, i) => (
                    <div key={b.id || i} className="flex gap-2 items-center">
                      <input className="flex-1 border rounded-lg px-3 py-2 text-sm" placeholder="Brand name (e.g. Fluarix)" value={b.name}
                        onChange={(e) => {
                          const next = [...vaxForm.brands];
                          next[i] = { ...next[i], name: e.target.value };
                          setVaxForm({ ...vaxForm, brands: next });
                        }} />
                      <span className="text-[11px] text-[#6b7280] whitespace-nowrap">
                        {b.indicative_price !== "" && b.indicative_price != null ? formatProductInr(b.indicative_price) : "—"}
                      </span>
                      <button type="button" className="text-[#ee4036] text-sm" onClick={() => {
                        const flushed = persistEditorToForm();
                        const next = (flushed.brands || []).filter((_, j) => j !== i);
                        const nextId = priceBrandId === b.id ? (next[0]?.id || "") : priceBrandId;
                        setPriceBrandId(nextId);
                        if (nextId) {
                          const brand = next.find((x) => x.id === nextId);
                          setCharges(mapChargeRows(brand?.charges, brand?.indicative_price));
                          setVaxForm({ ...flushed, brands: next, indicative_price: brand?.indicative_price ?? "" });
                        } else {
                          setVaxForm({ ...flushed, brands: next });
                        }
                      }}>Remove</button>
                    </div>
                  ))}
                  <button type="button" className="text-sm font-semibold text-[#673c96]"
                    onClick={() => {
                      const flushed = persistEditorToForm();
                      const nb = {
                        id: `brand-${Date.now()}`,
                        name: "",
                        indicative_price: flushed.indicative_price || "",
                        charges: serializeCharges(charges, flushed.indicative_price),
                      };
                      setVaxForm({ ...flushed, brands: [...(flushed.brands || []), nb], indicative_price: nb.indicative_price });
                      setPriceBrandId(nb.id);
                    }}>
                    + Add brand
                  </button>
                </div>
                <label className="block text-sm text-[#311b52]">
                  Description shown on the public catalogue
                  <textarea rows={2} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" value={vaxForm.description}
                    onChange={(e) => setVaxForm({ ...vaxForm, description: e.target.value })} />
                </label>
                {(vaxForm.brands || []).length > 0 && (
                  <div className="rounded-xl border border-[#673c96]/20 p-3 space-y-2" data-testid="vax-brand-price-targets">
                    <p className="text-sm font-semibold text-[#311b52]">Change price &amp; GST for</p>
                    <p className="text-xs text-[#6b7280]">Select one brand at a time. The other brands keep their own price and tax.</p>
                    {(vaxForm.brands || []).map((b) => (
                      <label key={b.id} className="flex items-center gap-2 text-sm text-[#311b52]">
                        <input type="radio" name="vax-brand-price" checked={priceBrandId === b.id} onChange={() => selectPriceBrand(b.id)}
                          data-testid={`vax-brand-price-${b.id}`} />
                        <span className="font-medium">{b.name || "Untitled brand"}</span>
                        <span className="text-xs text-[#6b7280]">
                          {b.indicative_price !== "" && b.indicative_price != null
                            ? `${formatProductInr(priceBrandId === b.id ? vaxForm.indicative_price : b.indicative_price)} · pays ${formatInr(previewTotal(priceBrandId === b.id ? charges : (b.charges || charges), Number((priceBrandId === b.id ? vaxForm.indicative_price : b.indicative_price)) || 0))}`
                            : "no price yet"}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
                <label className="block text-sm text-[#311b52]">
                  Product price (without GST) ₹
                  {(vaxForm.brands || []).length > 0 && (
                    <span className="block text-xs font-normal text-[#6b7280]">Applies only to {(vaxForm.brands || []).find((b) => b.id === priceBrandId)?.name || "the selected brand"}</span>
                  )}
                  <input type="number" min={0} step="1" className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
                    value={vaxForm.indicative_price}
                    onChange={(e) => {
                      const val = e.target.value;
                      setVaxForm({
                        ...vaxForm,
                        indicative_price: val,
                        brands: (vaxForm.brands || []).map((b) => (
                          b.id === priceBrandId ? { ...b, indicative_price: val } : b
                        )),
                      });
                    }} />
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={vaxForm.active !== false} onChange={(e) => setVaxForm({ ...vaxForm, active: e.target.checked })} />
                  Show on the public catalogue
                </label>
              </div>
            )}
            <p className="text-xs font-medium text-[#311b52]">GST &amp; extra charges{(vaxForm?.brands || []).length ? " for the selected brand" : ""}</p>
            <p className="text-xs text-[#6b7280]">
              {(vaxForm?.brands || []).length
                ? "These tax rows apply only to the selected brand. Other brands keep their own GST. "
                : ""}
              Product price is set once above. CGST and SGST apply only to that product price. Extra rows (surcharge etc.) are separate — their tax % applies to themselves only.
            </p>
            {charges.map((c, idx) => isBaseCharge(c) ? null : (
              <div key={c.id || idx} className="grid grid-cols-12 gap-2 items-center">
                <input className="col-span-4 border rounded-lg px-2 py-1.5 text-sm" value={c.label} onChange={(e) => patchCharge(idx, { label: e.target.value })} placeholder="Label (e.g. CGST)" data-testid={`charge-label-${idx}`} />
                <select className="col-span-2 border rounded-lg px-2 py-1.5 text-sm" value={c.calc} onChange={(e) => patchCharge(idx, { calc: e.target.value })} data-testid={`charge-calc-${idx}`}>
                  <option value="fixed">Fixed ₹</option>
                  <option value="percent">Percent %</option>
                </select>
                <input type="number" step="0.01" className="col-span-3 border rounded-lg px-2 py-1.5 text-sm" value={c.amount} onChange={(e) => patchCharge(idx, { amount: e.target.value })} data-testid={`charge-amount-${idx}`} />
                {c.calc !== "percent" ? (
                  <input type="number" step="0.1" className="col-span-2 border rounded-lg px-2 py-1.5 text-sm" title="Tax % on this extra charge"
                    value={c.tax_percent ?? 18} onChange={(e) => patchCharge(idx, { tax_percent: e.target.value, taxable: Number(e.target.value) > 0 })} placeholder="Tax %" />
                ) : <span className="col-span-2 text-[11px] text-[#9ca3af]">On product</span>}
                <button type="button" className="col-span-1 text-[#ee4036] text-sm" onClick={() => setCharges(charges.filter((_, i) => i !== idx))} aria-label="Remove row">×</button>
              </div>
            ))}
            <div className="flex flex-wrap gap-2">
              <button type="button" className="text-sm text-[#673c96] font-semibold" onClick={() => addChargeRow()} data-testid="charge-add-row">+ Add charge line</button>
              <button type="button" className="text-sm text-[#673c96] font-semibold" onClick={() => addChargeRow({ id: `cgst-${Date.now()}`, code: "cgst", label: "CGST", amount: 2.5, calc: "percent" })} data-testid="charge-add-cgst">+ CGST 2.5%</button>
              <button type="button" className="text-sm text-[#673c96] font-semibold" onClick={() => addChargeRow({ id: `sgst-${Date.now()}`, code: "sgst", label: "SGST", amount: 2.5, calc: "percent" })} data-testid="charge-add-sgst">+ SGST 2.5%</button>
            </div>
            <p className="text-sm font-semibold text-[#311b52]">
              Customer pays {formatInr(previewTotal(charges, Number(vaxForm?.indicative_price) || 0))}
              {(vaxForm?.brands || []).length && priceBrandId
                ? ` for ${(vaxForm.brands || []).find((b) => b.id === priceBrandId)?.name || "the selected brand"}`
                : ""}
            </p>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={closeVaccine} className="flex-1 border rounded-full py-2">Cancel</button>
              <button type="button" onClick={saveCharges} className="flex-1 bg-[#f37721] text-white rounded-full py-2 font-semibold">Save vaccine</button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8">
        <a className="text-[#f37721] text-sm font-semibold" href={`${config.API}/admin/export/bookings.csv`} target="_blank" rel="noreferrer">
          Download bookings CSV
        </a>
      </div>
    </div>
  );
}

function Panel({ title, onAdd, rows, columns }) {
  return (
    <div className="bg-white rounded-xl border border-[#673c96]/10 p-4">
      <div className="flex justify-between mb-4">
        <h2 className="font-semibold text-[#311b52]">{title}</h2>
        <button onClick={onAdd} className="text-sm bg-[#673c96] text-white px-3 py-1.5 rounded-md">Add</button>
      </div>
      <div className="overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[#673c96]">
              {columns.map((c) => <th key={c} className="py-2 pr-3">{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#673c96]/10">
                {columns.map((c) => <td key={c} className="py-2 pr-3 text-[#311b52]">{String(r[c] ?? "")}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <p className="text-[#673c96] text-sm py-4">No rows yet.</p>}
      </div>
    </div>
  );
}
